import { useTranslations } from '@vocab/react';
import {
  Button,
  IconAdd,
  Inline,
  Stack,
  Text,
  TextField,
} from 'braid-design-system';
import type React from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import type { DraftCustomQuestion } from '../DraftCustomQuestion/DraftCustomQuestion';

type Props = {
  options: string[];
  onChange: (question: Partial<DraftCustomQuestion>) => void;
};

const ANSWER_LIMIT = 7;
const ANSWER_CHAR_LIMIT = 150;

const answerExceedsCharLimit = (oldAnswer: string, newAnswer: string) =>
  newAnswer.length > oldAnswer.length && newAnswer.length > ANSWER_CHAR_LIMIT;

export const MultipleChoiceOptions: React.FC<Props> = ({
  options,
  onChange,
}) => {
  const { t } = useTranslations(translations);
  const addNewAnswer = () => onChange({ options: [...options, ''] });
  const updateAnswer = (position: number, answer: string) =>
    onChange({
      options: [
        ...options.slice(0, position),
        answer,
        ...options.slice(position + 1),
      ],
      error: null,
    });

  return (
    <Stack space="medium">
      <Text weight="medium">{t('custom.multiChoiceInfo')}</Text>

      <Stack space="medium">
        {options.map((text, i) => (
          <TextField
            placeholder={t('custom.answer.placeholder')}
            id={`answer-${i}`}
            aria-label={`Answer ${i + 1}`}
            label={t('custom.answer.label')}
            key={i}
            value={text}
            onChange={(e) => {
              const newAnswer = e.currentTarget.value;
              if (answerExceedsCharLimit(text, newAnswer)) {
                return;
              }

              updateAnswer(i, newAnswer);
            }}
          />
        ))}
      </Stack>

      {options.length < ANSWER_LIMIT ? (
        <Inline alignY="center" space="none">
          <Button
            variant="transparent"
            bleed
            icon={<IconAdd />}
            onClick={() => addNewAnswer()}
          >
            {t('custom.multiChoiceButton')}
          </Button>
        </Inline>
      ) : null}
    </Stack>
  );
};
